
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Save, Upload, User, Key } from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const ProfileTab: React.FC = () => {
  const { user } = useAuth();
  const { profile, loading, saving, updateProfile } = useProfile();
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    avatar_url: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [changingPassword, setChangingPassword] = useState(false);

  // Update form data when profile loads
  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        email: profile.email || user?.email || '',
        avatar_url: profile.avatar_url || ''
      });
    } else if (user && !loading) {
      // Initialize with user data if no profile exists
      setFormData({
        full_name: user.user_metadata?.full_name || user.user_metadata?.name || '',
        email: user.email || '',
        avatar_url: user.user_metadata?.avatar_url || ''
      });
    }
  }, [profile, user, loading]);

  const handleSave = async () => {
    const success = await updateProfile({
      full_name: formData.full_name.trim() || null,
      email: formData.email.trim() || null,
      avatar_url: formData.avatar_url.trim() || null
    });

    if (success) {
      // Force a refresh of the auth context if needed
      window.location.reload();
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setChangingPassword(true);
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) {
        toast.error('Failed to update password: ' + error.message);
      } else {
        toast.success('Password updated successfully');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      toast.error('An error occurred while updating password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const displayName = formData.full_name || formData.email || 'User';
  const initials = displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Profile Information
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Avatar Section */}
        <div className="flex items-center gap-4">
          <Avatar className="h-20 w-20">
            <AvatarImage src={formData.avatar_url} alt={displayName} />
            <AvatarFallback className="bg-gradient-to-br from-green-600 to-emerald-700 text-white text-lg">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="space-y-2">
            <Label htmlFor="avatar_url">Avatar URL</Label>
            <div className="flex gap-2">
              <Input
                id="avatar_url"
                value={formData.avatar_url}
                onChange={(e) => handleInputChange('avatar_url', e.target.value)}
                placeholder="https://example.com/avatar.jpg"
                className="flex-1"
              />
              <Button variant="outline" size="sm">
                <Upload className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Form Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="full_name">Display Name *</Label>
            <Input
              id="full_name"
              value={formData.full_name}
              onChange={(e) => handleInputChange('full_name', e.target.value)}
              placeholder="Enter your display name"
              required
            />
            <p className="text-xs text-muted-foreground">
              This will replace your Twitter username throughout the app
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="Enter your email"
            />
            <p className="text-xs text-muted-foreground">
              This email will be used for notifications
            </p>
          </div>
        </div>

        {/* Current Profile Info */}
        {profile && (
          <div className="pt-4 border-t">
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Current Profile</h4>
            <div className="text-sm space-y-1">
              <p><span className="font-medium">Name:</span> {profile.full_name || 'Not set'}</p>
              <p><span className="font-medium">Email:</span> {profile.email || 'Not set'}</p>
              <p><span className="font-medium">Last updated:</span> {new Date(profile.updated_at).toLocaleDateString()}</p>
            </div>
          </div>
        )}

        {/* Password Change Section */}
        <div className="pt-6 border-t">
          <h4 className="text-lg font-medium mb-4 flex items-center gap-2">
            <Key className="h-5 w-5" />
            Change Password
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                placeholder="Enter new password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                placeholder="Confirm new password"
              />
            </div>
          </div>
          <Button 
            onClick={handlePasswordChange}
            disabled={changingPassword || !passwordData.newPassword || !passwordData.confirmPassword}
            className="mt-4"
            variant="outline"
          >
            <Key className="h-4 w-4 mr-2" />
            {changingPassword ? 'Updating...' : 'Update Password'}
          </Button>
        </div>

        {/* Save Button */}
        <div className="flex gap-2 pt-4">
          <Button 
            onClick={handleSave} 
            disabled={saving || !formData.full_name.trim()}
            className="flex-1 sm:flex-none"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Profile'}
          </Button>
          
          {!formData.full_name.trim() && (
            <p className="text-xs text-muted-foreground self-center">
              Display name is required
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfileTab;
